import LandingFooter from '@/components/LandingFooter.tsx'
import LandingHeader from '@/components/LandingHeader.tsx'

import { cn } from '@/lib/utils.ts'
import type { Language } from '@/utils/language.ts'
import { useState } from 'react'
import { NavLink } from 'react-router-dom'

export const LandingPage = () => {
  const [language, setLanguage] = useState<Language>('en')

  const featureCards = [
    {
      id: 'feature-1',
      icon: '/images/landing-tracker-boot-logo-1.svg',
      title: 'Dive\nRight In',
      description: 'No complex setup or steep learning curve. Just write your first story and start building.',
    },
    {
      id: 'feature-2',
      icon: '/images/landing-tracker-boot-logo-2.svg',
      title: 'Agile to the\nCore',
      description: 'Built on real Agile principles. Move fast, release often, adapt quickly.',
    },
    {
      id: 'feature-3',
      icon: '/images/landing-tracker-boot-logo-3.svg',
      title: 'Familiar\nbut Better',
      description: 'All the clarity of Pivotal Tracker, now with a more intuitive experience.',
    },
    {
      id: 'feature-4',
      icon: '/images/landing-tracker-boot-logo-4.svg',
      title: 'Seamlessly\nCollaborate',
      description: 'Stay aligned with real-time updates, visual flow, and shared priorities.',
    },
  ]

  return (
    <div className="bg-[#f7f7f7] overflow-x-hidden">
      <style>
        {`
          .landing-background {
            background-image: url(/images/landing/background/landing_background_mobile_1x.webp);
            background-size: 100% 4467px;
            background-position: top;
            background-repeat: no-repeat;
          }

          @media (min-width: 768px) {
            .landing-background {
              background-image: url(/images/landing/background/landing_background_pc_1x.webp);
            }
          }

          @media (-webkit-min-device-pixel-ratio: 2), (min-resolution: 192dpi) {
            .landing-background {
              background-image: url(/images/landing/background/landing_background_mobile_2x.webp);
            }
          }

          @media (min-width: 768px) and (-webkit-min-device-pixel-ratio: 2), 
                 (min-width: 768px) and (min-resolution: 192dpi) {
            .landing-background {
              background-image: url(/images/landing/background/landing_background_pc_2x.webp);
            }
          }
        `}
      </style>
      <div className="landing-background">
        <div className="relative z-10 max-w-7xl mx-auto px-4">
          <LandingHeader language={language} setLanguage={setLanguage} selectLanguageEnabled={false} />
          <section className="py-16 flex flex-col md:flex-row items-center gap-8">
            <div className="relative w-full flex flex-row">
              <div className="w-full flex flex-col max-[1270px]:justify-center max-[1270px]:items-center">
                <div className="space-y-4">
                  <img
                    className="w-full max-w-[609px] h-[185px]"
                    alt="Main copy vectors"
                    src="/images/landing-title.svg"
                  />
                </div>
                <div className="w-full min-[1270px]:absolute min-[1270px]:right-0 min-[1270px]:top-0 max-[1270px]:mt-8">
                  <div
                    className={cn(
                      'w-full h-[349px] rounded-[60px]',
                      'bg-[url(/images/landing/main-image/en/main-image-en_1x.webp)] bg-cover bg-center bg-no-repeat max-[1270px]:bg-right',
                      'min-[1270px]:absolute min-[1270px]:w-[605px] min-[1270px]:right-0 min-[1270px]:[mask-image:url(/images/landing-main-image-mask.svg)] min-[1270px]:mask-no-repeat min-[1270px]:mask-center',
                    )}
                  />
                </div>
                <div className="min-[1270px]:w-[825px] min-[1270px]:mt-8">
                  <div className="flex items-center max-[650px]:flex-col gap-6 p-6">
                    <p className="flex-1 [font-family:'Pretendard-Medium',Helvetica] font-medium text-[#283139] text-xl tracking-[-0.50px] leading-[31.0px]">
                      Accelerate your success with Tracker Boot—designed for faster, smarter, and more efficient product
                      development. The new standard in agile collaboration. Now available. Sign up and start building.
                    </p>
                    <NavLink
                      to="/login"
                      className="px-6 py-5 bg-[#ff8577] rounded-2xl [font-family:'Pretendard-Bold',Helvetica] font-bold text-white text-xl tracking-[-0.50px] leading-[24.0px] z-10"
                    >
                      Get started
                    </NavLink>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="py-20 space-y-12">
            <div className="text-center space-y-2">
              <h2 className="text-[#131e28] text-[52px] tracking-[-1.30px] leading-[72.8px] [font-family:'Pretendard-Bold',Helvetica] font-bold">
                Tracker Boot
              </h2>
              <p className="text-[#131e28] text-[32px] tracking-[-0.80px] leading-[44.8px] [font-family:'Pretendard-Regular',Helvetica] font-normal">
                Tailored for Agile Teams
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 min-[1145px]:grid-cols-4 gap-px">
              {featureCards.map((card) => (
                <div key={card.id} className="bg-white rounded-[60px] border-none whitespace-break-spaces">
                  <div className="flex flex-col items-start gap-3 p-14">
                    <img className="w-[66px] h-[60px] rounded-md" src={card.icon} alt={card.title} />
                    <div className="flex flex-col items-start gap-4 pt-5">
                      <h3 className="h-[90px] text-[#283139] text-[32px] tracking-[-0.80px] leading-[44.8px] [font-family:'Pretendard-Bold',Helvetica] font-bold">
                        {card.title}
                      </h3>
                      <p className="text-[#283139] text-lg tracking-[-0.27px] leading-[25.2px] [font-family:'Pretendard-Regular',Helvetica] font-normal">
                        {card.description}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section className="py-20">
            <h2 className="text-[#283139] text-[52px] tracking-[-1.30px] leading-[72.8px] [font-family:'Pretendard-Bold',Helvetica] font-bold mb-16">
              Accelerating Teams since 1989
            </h2>
            <div className="relative flex flex-col md:flex-row gap-8">
              <div className="w-full md:w-1/3">
                <img src="/images/landing-past-logos-rotating.svg" alt="tracker by pivotal & pivotal labs" />
              </div>
              <div className="w-full md:w-2/3">
                <p className="text-[#283139] text-xl tracking-[0] leading-[31.0px] [font-family:'Pretendard-Regular',Helvetica] font-normal text-right">
                  Tracker Boot traces its roots to Pivotal Tracker, the product management tool developed by Pivotal
                  Labs, a Silicon Valley pioneer in Agile development. <br />
                  Now, Tracker Boot continues that legacy. Developed by Bekind Labs, <br />
                  founded by former Pivotal Labs team members, it inherits Pivotal Tracker&apos;s core functionalities
                  while enabling seamless data migration for a smooth transition. Built for rapid releases, continuous
                  iteration, and adaptability to change, Tracker Boot empowers product teams to deliver value faster and
                  more efficiently.
                </p>
                <img
                  className="w-full max-w-[818px] h-auto mt-8"
                  alt="Element"
                  src="/images/landing-pivotal-labs-year.svg"
                />
              </div>
            </div>
          </section>

          <section className="relative py-20 flex flex-col md:flex-row items-center justify-between gap-8">
            <div className="w-full md:w-1/2 space-y-6">
              <h2 className="text-[#283139] text-4xl tracking-[-0.90px] leading-[50.4px] [font-family:'Pretendard-Bold',Helvetica] font-bold">
                Products are Built from Stories,&nbsp;&nbsp;not Tickets
              </h2>
              <p className="text-[#283139] text-[22px] tracking-[-0.55px] leading-[30.8px] [font-family:'Pretendard-Regular',Helvetica] font-normal">
                You know good products come from tuning into stories from people, understanding their pain points, and
                solving for them. Tracker Boot knows this too and puts user stories at its forefront so your team can
                start thinking about what to solve right away.
              </p>
            </div>
            <div className="w-full md:w-1/2 relative sm:flex sm:flex-row sm:justify-center sm:items-center">
              <img
                className="w-full max-w-[570px] h-[360px] object-contain"
                alt="story-feature"
                src="/images/landing/landing_feature_story/landing_feature_story_1x.webp"
                srcSet="/images/landing/landing_feature_story/landing_feature_story_1x.webp 1x, /images/landing/landing_feature_story/landing_feature_story_2x.webp 2x"
              />
            </div>
            <div className="absolute z-[-1] bg-white w-[1883.453px] h-[523px] rotate-[3.373deg] rounded-[100px] translate-x-[-100px]" />
          </section>

          <section className="relative py-20 flex flex-col md:flex-row-reverse items-center justify-between gap-8">
            <div className="w-full md:w-1/2 space-y-6">
              <h2 className="text-[#283139] text-4xl tracking-[-0.90px] leading-[50.4px] [font-family:'Pretendard-Bold',Helvetica] font-bold">
                Iterate more Intelligently
              </h2>
              <p className="text-[#283139] text-[22px] tracking-[-0.33px] leading-[30.8px] [font-family:'Pretendard-Regular',Helvetica] font-normal">
                A strong product team can continuously deliver value at a sustainable pace. Tracker Boot helps plan
                iterations by automatically calculating realistic delivery estimates and achievability of release goals
                based on your team&apos;s actual development pace.
              </p>
            </div>
            <div className="w-full md:w-1/2 relative sm:flex sm:flex-row sm:justify-center sm:items-center">
              <img
                className="w-full max-w-[570px] h-[629px] object-contain"
                alt="iteration-feature"
                src="/images/landing/landing_feature_iteration/landing_feature_iteration_1x.webp"
                srcSet="/images/landing/landing_feature_iteration/landing_feature_iteration_1x.webp 1x, /images/landing/landing_feature_iteration/landing_feature_iteration_2x.webp 2x"
              />
            </div>
            <div className="absolute z-[-1] bg-white w-[1763.412px] h-[517.833px] rotate-[-3.842deg] rounded-[100px] translate-x-[50px]" />
          </section>

          <section className="py-20 space-y-12">
            <div className="text-center space-y-6">
              <h2 className="text-[#283139] text-4xl tracking-[-0.90px] leading-[50.4px] [font-family:'Pretendard-Bold',Helvetica] font-bold">
                Analyze and Adapt Your Process
              </h2>
              <p className="text-[#283139] text-[22px] tracking-[-0.33px] leading-[30.8px] [font-family:'Pretendard-Regular',Helvetica] font-normal">
                You need insights into the team&apos;s state of development to ensure continuous delivery. Tracker Boot
                helps you analyze and visualize development trends, so you can make data-driven decisions on how to
                improve your process.
              </p>
            </div>
            <img
              className="w-full max-w-[1261px] h-auto sm:flex sm:flex-row sm:justify-center sm:items-center"
              alt="analytics-feature"
              src="/images/landing/landing_feature_analytics/landing_feature_analytics_1x.webp"
              srcSet="/images/landing/landing_feature_analytics/landing_feature_analytics_1x.webp 1x, /images/landing/landing_feature_analytics/landing_feature_analytics_2x.webp 2x"
            />
          </section>

          <section className="py-20 space-y-12">
            <div className="flex flex-col items-center space-y-4">
              <img className="w-[676px] h-[91px]" alt="Logowhite" src="/images/landing-tracker-boot-logo.svg" />
              <p className="[font-family:'Pretendard-Bold',Helvetica] font-bold text-[#ff8577] text-[32px] text-center tracking-[-0.80px] leading-[48px]">
                The Official Tool for Agile Success
              </p>
            </div>

            <p className="text-[#283139] text-lg text-center tracking-[-0.45px] leading-[27.9px] [font-family:'Pretendard-Regular',Helvetica] font-normal">
              Together with Tracker Boot, Bekind Labs transforms the way you build and manage products. <br />
              Bekind Labs&apos; consulting services empower your team to excel in product development, app
              modernization, and organizational transformation, <br />
              applying Silicon Valley&apos;s best development practices—Lean Startup, Extreme Programming, and Balanced
              Team.&nbsp;&nbsp;Whether you&apos;re refining your workflow or scaling your development process, our
              expertise and tools drive faster delivery and greater impact.
            </p>

            <div className="w-full h-[1px] border border-solid border-t-[#D2D2D2]" />

            <div className="flex flex-col items-center space-y-12">
              <img className="w-[222px] h-[85px]" alt="Logo full color" src="/images/landing-bekind-labs-logo.svg" />
              <a
                href="https://bekindlabs.com/"
                target="_blank"
                rel="noreferrer"
                className="px-6 py-5 rounded-2xl border border-solid border-[#28599e] [font-family:'Pretendard-Bold',Helvetica] font-bold text-[#28599e] text-xl tracking-[-0.50px] leading-5"
              >
                Learn more about Bekind Labs
              </a>
            </div>
          </section>
        </div>
        <LandingFooter />
      </div>
    </div>
  )
}
